/**
 * ss-home-carousel.js
 * ─────────────────────────────────────────────────────────────────
 * Adds left/right arrow navigation to:
 *   1. Bestsellers product grids (.ss-bs-grid) — one per category tab
 *   2. Mineral skincare grid (.ss-mineral-grid)
 *
 * Arrow visibility rules:
 *   - LEFT arrow hidden when scrolled to the very start
 *   - RIGHT arrow hidden when scrolled to the very end
 *   - Both hidden when all content fits without scrolling
 *
 * FIX (mobile card-snap):
 *   - CSS scroll-snap is injected at runtime (scroll-snap-type on grid,
 *     scroll-snap-align on each card) so each arrow press lands exactly
 *     on a card boundary.
 *   - Scroll amount = first visible card's offsetWidth + computed column-gap
 *     (no more fixed ratio that misses card edges).
 * ─────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  /* ── SVG icons ─────────────────────────────────────────────── */
  const ICON_PREV = `<svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>`;
  const ICON_NEXT = `<svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>`;

  /**
   * createArrowBtn
   * Builds and returns an arrow button element.
   * @param {'prev'|'next'} direction
   */
  function createArrowBtn(direction) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `ss-carousel-btn ss-carousel-btn--${direction} is-hidden`;
    btn.setAttribute('aria-label', direction === 'prev' ? 'Scroll left' : 'Scroll right');
    btn.innerHTML = direction === 'prev' ? ICON_PREV : ICON_NEXT;
    return btn;
  }

  /**
   * getCardStep
   * Measures the exact scroll distance needed to advance one card:
   *   card.offsetWidth + computed column-gap on the grid.
   * Falls back to 85% of grid width if no card is found.
   *
   * @param {HTMLElement} grid
   * @returns {number} pixels to scroll
   */
  function getCardStep(grid) {
    /* Pick the first *visible* product card */
    const card = grid.querySelector('.ss-product-card:not(.hidden):not(.is-hidden)');
    if (card) {
      const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
      return card.offsetWidth + gap;
    }
    /* Fallback */
    return Math.round(grid.clientWidth * 0.85);
  }

  /**
   * applySnapToCards
   * Sets scroll-snap-align: start on every product card inside the grid.
   * Safe to call multiple times (idempotent).
   *
   * @param {HTMLElement} grid
   */
  function applySnapToCards(grid) {
    grid.querySelectorAll('.ss-product-card').forEach(function (card) {
      card.style.scrollSnapAlign = 'start';
    });
  }

  /**
   * initCarousel
   * Wraps a scrollable grid in a .ss-carousel-wrapper, enables CSS
   * scroll-snap on it, and wires up the prev/next buttons.
   *
   * @param {HTMLElement} grid        The scrollable flex container
   * @param {string}      wrapId      Optional id for the wrapper (for dedup)
   * @param {string}      wrapperClass Extra class on the wrapper div
   */
  function initCarousel(grid, wrapId, wrapperClass) {
    /* Avoid double-init */
    if (grid.dataset.carouselInit === '1') return;
    grid.dataset.carouselInit = '1';

    /* ── Inject scroll-snap so the browser snaps to card edges ── */
    grid.style.scrollSnapType = 'x mandatory';
    applySnapToCards(grid);

    /* Build wrapper */
    const wrapper = document.createElement('div');
    wrapper.className = 'ss-carousel-wrapper ' + (wrapperClass || '');
    if (wrapId) wrapper.id = wrapId;

    const btnPrev = createArrowBtn('prev');
    const btnNext = createArrowBtn('next');

    /* Insert wrapper around the grid */
    grid.parentNode.insertBefore(wrapper, grid);
    wrapper.appendChild(btnPrev);
    wrapper.appendChild(grid);
    wrapper.appendChild(btnNext);

    /* ── Update arrow visibility ─────────────────────────────── */
    function updateArrows() {
      const scrollLeft = Math.round(grid.scrollLeft);
      const maxScroll  = grid.scrollWidth - grid.clientWidth;

      /* No scroll needed at all */
      if (maxScroll <= 2) {
        btnPrev.classList.add('is-hidden');
        btnNext.classList.add('is-hidden');
        return;
      }

      /* At the start — hide left */
      btnPrev.classList.toggle('is-hidden', scrollLeft <= 2);
      /* At the end — hide right */
      btnNext.classList.toggle('is-hidden', scrollLeft >= maxScroll - 2);
    }

    /* ── Scroll by exactly one card ─────────────────────────── */
    function scrollByOneCard(direction) {
      const step   = getCardStep(grid);
      const target = grid.scrollLeft + (direction === 'next' ? step : -step);
      /* scrollTo with the computed target; scroll-snap snaps it to the
         nearest card edge, guaranteeing no partial-card cutoff.        */
      grid.scrollTo({ left: target, behavior: 'smooth' });
    }

    /* ── Events ──────────────────────────────────────────────── */
    btnPrev.addEventListener('click', () => scrollByOneCard('prev'));
    btnNext.addEventListener('click', () => scrollByOneCard('next'));

    grid.addEventListener('scroll', updateArrows, { passive: true });

    /* Re-check on resize (content reflow / breakpoint change) */
    const ro = new ResizeObserver(function () {
      applySnapToCards(grid); /* picks up any newly rendered cards */
      updateArrows();
    });
    ro.observe(grid);

    /* Initial state */
    updateArrows();
  }

  /* ────────────────────────────────────────────────────────────
     1. BESTSELLERS — one .ss-bs-grid per category tab.
        Each grid already exists in the DOM; tabs just toggle
        display. We init all grids on load.
  ──────────────────────────────────────────────────────────── */
  function initBestsellersCarousels() {
    const grids = document.querySelectorAll('.ss-bs-grid');
    grids.forEach(function (grid, idx) {
      initCarousel(grid, 'ss-bs-carousel-' + idx, 'ss-carousel-wrapperB');
    });
  }

  /* ────────────────────────────────────────────────────────────
     2. MINERAL GRID — single .ss-mineral-grid
  ──────────────────────────────────────────────────────────── */
  function initMineralCarousel() {
    const grid = document.querySelector('.ss-mineral-grid');
    if (grid) initCarousel(grid, 'ss-mineral-carousel', 'ss-carousel-wrapperA');
  }

  /* ────────────────────────────────────────────────────────────
     3. MINERAL SECTION — existing per-section setup
        (preserved exactly as original)
  ──────────────────────────────────────────────────────────── */
  document.querySelectorAll('.ss-mineral-section').forEach((section) => {
    const grid = section.querySelector('.ss-mineral-grid');
    if (!grid) return;

    let wrapper = grid.closest('.ss-carousel-wrapperA');

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'ss-carousel-wrapperA';
      grid.parentNode.insertBefore(wrapper, grid);
      wrapper.appendChild(grid);
    }

    wrapper.querySelectorAll('.ss-carousel-btn--prev').forEach((btn, index) => {
      if (index > 0) btn.remove();
    });

    wrapper.querySelectorAll('.ss-carousel-btn--next').forEach((btn, index) => {
      if (index > 0) btn.remove();
    });

    let prevBtn = wrapper.querySelector('.ss-carousel-btn--prev');
    let nextBtn = wrapper.querySelector('.ss-carousel-btn--next');

    if (!prevBtn) {
      prevBtn = document.createElement('button');
      prevBtn.className = 'ss-carousel-btn ss-carousel-btn--prev';
      prevBtn.setAttribute('type', 'button');
      prevBtn.setAttribute('aria-label', 'Previous products');
      prevBtn.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 18l-6-6 6-6"></path>
        </svg>
      `;
      wrapper.appendChild(prevBtn);
    }

    if (!nextBtn) {
      nextBtn = document.createElement('button');
      nextBtn.className = 'ss-carousel-btn ss-carousel-btn--next';
      nextBtn.setAttribute('type', 'button');
      nextBtn.setAttribute('aria-label', 'Next products');
      nextBtn.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 6l6 6-6 6"></path>
        </svg>
      `;
      wrapper.appendChild(nextBtn);
    }

    /* Apply scroll-snap to the mineral grid as well */
    grid.style.scrollSnapType = 'x mandatory';
    grid.querySelectorAll('.ss-sub-card').forEach(function (card) {
      card.style.scrollSnapAlign = 'start';
    });

    const updateButtons = () => {
      const maxScroll = grid.scrollWidth - grid.clientWidth;
      prevBtn.classList.toggle('is-hidden', grid.scrollLeft <= 4);
      nextBtn.classList.toggle('is-hidden', grid.scrollLeft >= maxScroll - 4);
    };

    /* Mineral grid uses .ss-sub-card — measure that */
    const getMineralStep = () => {
      const card = grid.querySelector('.ss-sub-card');
      if (card) {
        const gap = parseFloat(getComputedStyle(grid).columnGap) || 30;
        return card.offsetWidth + gap;
      }
      return Math.round(grid.clientWidth * 0.85);
    };

    prevBtn.addEventListener('click', () => {
      grid.scrollTo({ left: grid.scrollLeft - getMineralStep(), behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      grid.scrollTo({ left: grid.scrollLeft + getMineralStep(), behavior: 'smooth' });
    });

    grid.addEventListener('scroll', updateButtons);
    window.addEventListener('resize', updateButtons);

    updateButtons();
  });

  /* ────────────────────────────────────────────────────────────
     Re-run arrow update when a bestsellers tab is clicked.
     (The active grid changes from display:none → display:flex,
      so its scroll metrics need a fresh read.)
  ──────────────────────────────────────────────────────────── */
  function watchBestsellerTabs() {
    const tabsContainer = document.querySelector('.ss-bs-tabs');
    if (!tabsContainer) return;

    tabsContainer.addEventListener('click', function (e) {
      const tab = e.target.closest('.ss-bs-tab');
      if (!tab) return;

      /* Wait one rAF for the grid to become visible */
      requestAnimationFrame(function () {
        const activeGrid = document.querySelector('.ss-bs-grid:not([style*="display: none"]):not(.hidden)');
        if (!activeGrid) return;

        /* If not yet init'd (shouldn't happen but safe) */
        initCarousel(activeGrid, '', 'ss-carousel-wrapperB');

        /* Re-apply snap to any cards that became visible */
        applySnapToCards(activeGrid);

        /* Force arrow re-evaluation after layout */
        const wrapper = activeGrid.closest('.ss-carousel-wrapper');
        if (!wrapper) return;

        const btnPrev = wrapper.querySelector('.ss-carousel-btn--prev');
        const btnNext = wrapper.querySelector('.ss-carousel-btn--next');
        if (!btnPrev || !btnNext) return;

        const scrollLeft = Math.round(activeGrid.scrollLeft);
        const maxScroll  = activeGrid.scrollWidth - activeGrid.clientWidth;

        if (maxScroll <= 2) {
          btnPrev.classList.add('is-hidden');
          btnNext.classList.add('is-hidden');
        } else {
          btnPrev.classList.toggle('is-hidden', scrollLeft <= 2);
          btnNext.classList.toggle('is-hidden', scrollLeft >= maxScroll - 2);
        }
      });
    });
  }

  /* ────────────────────────────────────────────────────────────
     Boot
  ──────────────────────────────────────────────────────────── */
  function init() {
    initBestsellersCarousels();
    initMineralCarousel();
    watchBestsellerTabs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
